import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Grid,
  InputGroup,
  Dialog,
  Heading,
  majorScale,
  FormField,
  Checkbox,
  TextArea,
  defaultColors,
  Button,
  TagInput,
  HTMLSelect,
  useToast,
  NumberInput,
  EmptyInputGroup,
  ControlGroup,
} from '../../npaper';
import { AddressFormControls } from '../../npaper/AddressFormControls';
import { CurrencySelect } from '../../npaper/CurrencySelect';
import { DateInput } from '../../npaper/DateInput';
import { formatTermType } from '../../npaper/FormattedTerms';
import { UsersInput } from '../../npaper/UserSelect';
import {
  DeliveryType,
  formatDeliveryType,
  ISale,
  ISaleSnapsnotIn,
  PaymentTermsType,
  SaleStatus,
  SaleType,
  useStores,
} from '../../stores';
import { CompanySelect } from '../companies/CompanySelect';
import { ContactsInput } from '../contacts/ContactSelect';
import { saleTypeTitle } from './SaleTypeTitle';

type FormValues = ISaleSnapsnotIn & {
  useDefaultAddress: boolean;
  sameAddressAsInvoice: boolean;
};

export interface ISaleDetailDialogProps {
  isShown: boolean;
  onClose: (sale?: ISale) => void;
  sale?: ISale;
  defaultValue?: Partial<FormValues>;
}

export const SaleDetailDialog: React.FC<ISaleDetailDialogProps> = ({
  sale,
  defaultValue = {},
  ...props
}) => {
  const intl = useIntl();
  const toaster = useToast();
  const [isShown, setIsShown] = useState(props.isShown);
  const [created, setCreated] = useState<ISale>();
  useEffect(() => {
    setIsShown(props.isShown);
  }, [props.isShown]);

  const close = () => {
    setIsShown(false);
  };

  const { saleStore, saleItemStore, authStore, companyStore } = useStores();

  const lockOrder =
    sale &&
    ((sale.type === SaleType.ORDER && sale.status === SaleStatus.DRAFT) ||
      sale.type === SaleType.INVOICE);
  const lockInvoice =
    sale && sale.type === SaleType.INVOICE && sale.status !== SaleStatus.DRAFT;

  useEffect(() => {
    if (!isShown) return;
    if (sale) {
      const data = getSnapshot(sale);
      reset({
        ...data,
        useDefaultAddress: !data.invoice_address,
        sameAddressAsInvoice: !data.delivery_address,
      });
    } else {
      if (defaultValue.customer) {
        companyStore.getItem(defaultValue.customer).then((customer) => {
          reset({
            ...defaultValue,
            payment_terms_type: customer.payment_terms_type,
            days_of_credit: customer.days_of_credit,
          });
        });
      } else {
        reset(defaultValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale, isShown]);

  const methods = useForm<FormValues>();
  const {
    register,
    control,
    setValue,
    reset,
    errors,
    formState,
    handleSubmit,
    watch,
  } = methods;

  const onSubmit = async ({
    useDefaultAddress,
    sameAddressAsInvoice,
    ...values
  }: FormValues) => {
    if (useDefaultAddress) {
      delete values.invoice_address;
    }
    if (sameAddressAsInvoice) {
      delete values.delivery_address;
    }

    // Create or update...
    if (sale) {
      // Update
      try {
        await saleStore.updateItem(sale, values);
        saleItemStore.invalidateCaches();
        close();
      } catch (err) {
        // TODO: Handle error
        handleError(err);
      }
    } else {
      // Create
      try {
        const sale = await saleStore.createItem(values);
        setCreated(sale);
        close();
      } catch (err) {
        // TODO: Handle error
        handleError(err);
      }
    }
  };

  const handleError = (error: any) => {
    if (error.response) {
      if (error.response.data && error.response.data['delivery_address']) {
        toaster.addToast({
          message: JSON.stringify(error.response.data['delivery_address']),
          intent: 'danger',
        });
        return;
      }
      if (error.response.data && error.response.data['invoice_address']) {
        toaster.addToast({
          message: JSON.stringify(error.response.data['invoice_address']),
          intent: 'danger',
        });
        return;
      }
    }

    toaster.showDefaultError(error);
  };

  return (
    <Dialog
      title={
        sale
          ? intl.formatMessage(
              {
                defaultMessage: 'Edit {saleType}',
              },
              {
                saleType: saleTypeTitle(intl, sale.type),
              }
            )
          : intl.formatMessage({
              defaultMessage: 'Create offer',
            })
      }
      isShown={isShown}
      onClose={() => props.onClose(created)}
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {sale ? (
              <FormattedMessage defaultMessage="Save changes" />
            ) : (
              <FormattedMessage defaultMessage="Create offer" />
            )}
          </Button>
        </>
      }
    >
      <FormProvider {...methods}>
        <Grid padding={majorScale(2)}>
          <Heading size={300} className="col-span-12">
            <FormattedMessage defaultMessage="Details" />
          </Heading>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Title' })}
            inline={true}
          >
            <InputGroup
              placeholder={intl.formatMessage(
                {
                  defaultMessage: 'Title of {saleType}',
                },
                {
                  saleType: saleTypeTitle(
                    intl,
                    sale ? sale.type : SaleType.OFFER
                  ),
                }
              )}
              isInvalid={!!errors.title}
              validationMessage={errors.title && errors.title.message}
              ref={register({
                required: intl.formatMessage({
                  defaultMessage: 'Please provide a short title',
                }),
              })}
              name="title"
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({
              defaultMessage: 'Customer',
              description: 'Form field label',
            })}
            inline={true}
            isInvalid={!!errors.customer}
            hint={errors.customer && errors.customer.message}
          >
            <Controller
              control={control}
              name="customer"
              defaultValue={null}
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Please select a customer',
                  description: 'Input validation required',
                }),
              }}
              render={({ onChange, value }) => (
                <CompanySelect
                  onSelect={(company) => {
                    onChange(company.id);
                    if (company.currency) {
                      setValue('currency', company.currency);
                    }
                    if (company.address) {
                      setValue('invoice_address', company.address);
                    } else {
                      setValue('useDefaultAddress', false);
                    }
                    setValue('payment_terms_type', company.payment_terms_type);
                    setValue('days_of_credit', company.days_of_credit);
                  }}
                  selected={value}
                />
              )}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({
              defaultMessage: 'Contact persons',
            })}
            inline={true}
            hint={intl.formatMessage({
              defaultMessage: 'Add one or more contact persons to the sale',
            })}
          >
            <Controller
              control={control}
              name="contacts"
              defaultValue={[]}
              render={({ onChange, value, onBlur }) => (
                <ContactsInput
                  onBlur={onBlur}
                  value={value}
                  filter={{ company: watch('customer') }}
                  onChange={(contacts) => {
                    onChange(contacts);
                  }}
                />
              )}
            />
          </FormField>

          {!lockInvoice && sale && sale.type === SaleType.INVOICE && (
            <FormField
              className="col-span-9"
              label={intl.formatMessage({ defaultMessage: 'Invoice date' })}
              inline={true}
            >
              <Controller
                control={control}
                name="invoice_date"
                defaultValue={null}
                render={({ onChange, value }) => (
                  <DateInput
                    value={value}
                    onChange={(date) => {
                      onChange(date);
                    }}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Specify invoice date',
                    })}
                  />
                )}
              />
            </FormField>
          )}

          {!lockInvoice && (
            <FormField
              className="col-span-9"
              label={intl.formatMessage({
                defaultMessage: 'Payment terms',
                description: 'Form field label',
              })}
              inline={true}
            >
              <ControlGroup>
                <Controller
                  control={control}
                  name="payment_terms_type"
                  defaultValue={PaymentTermsType.NET}
                  rule={{
                    required: intl.formatMessage({
                      defaultMessage: 'Please provide a payment term type',
                      description: 'Input field validation required',
                    }),
                  }}
                  render={({ onChange, value }) => (
                    <HTMLSelect
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Choose type',
                        description: 'Input field placeholder',
                      })}
                      flexGrow={1}
                      options={Object.values(PaymentTermsType).map((value) => ({
                        label: formatTermType(intl, value),
                        value,
                      }))}
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="days_of_credit"
                  defaultValue={8}
                  render={({ onChange, value, onBlur }) => (
                    <NumberInput
                      width={60}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                      onChange={onChange}
                      value={value}
                      onBlur={onBlur}
                      display={
                        [
                          PaymentTermsType.CUSTOM,
                          PaymentTermsType.NET_CASH,
                        ].indexOf(watch('payment_terms_type')) > -1
                          ? 'none'
                          : undefined
                      }
                    />
                  )}
                />
                <EmptyInputGroup
                  display={
                    [
                      PaymentTermsType.CUSTOM,
                      PaymentTermsType.NET_CASH,
                    ].indexOf(watch('payment_terms_type')) > -1
                      ? 'none'
                      : undefined
                  }
                >
                  <FormattedMessage
                    defaultMessage="{days, plural, =1 {day} other {days}}"
                    values={{ days: watch('days_of_credit') }}
                  />
                </EmptyInputGroup>
              </ControlGroup>
              <Controller
                control={control}
                name="due_date"
                rules={{
                  validate: (v) =>
                    // If type is custom, we require the due date to be set.
                    watch('payment_terms_type') === PaymentTermsType.CUSTOM
                      ? !!v
                      : true,
                }}
                render={({ onChange, value }) => (
                  <Box
                    display={
                      watch('payment_terms_type') === PaymentTermsType.CUSTOM
                        ? undefined
                        : 'none'
                    }
                  >
                    <DateInput
                      value={value}
                      minDate={new Date()}
                      onChange={(date) => {
                        onChange(date);
                      }}
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Specify due date',
                      })}
                    />
                  </Box>
                )}
              />
              <TextArea
                placeholder={intl.formatMessage({
                  defaultMessage: 'Write your custom payment terms here',
                })}
                display={
                  watch('payment_terms_type') === PaymentTermsType.CUSTOM
                    ? undefined
                    : 'none'
                }
                name="custom_payment_terms"
                ref={register}
              />
            </FormField>
          )}

          {!lockInvoice && (
            <FormField
              className="col-span-9"
              label={intl.formatMessage({
                defaultMessage: 'Currency',
              })}
              inline={true}
            >
              <Controller
                control={control}
                name="currency"
                defaultValue={authStore.currentOrganisation!.default_currency}
                render={({ onChange, value }) => (
                  <CurrencySelect value={value} onChange={onChange} />
                )}
              />
            </FormField>
          )}

          {!lockOrder && (
            <FormField
              className="col-span-9"
              label={intl.formatMessage({
                defaultMessage: 'Delivery type',
              })}
              inline={true}
            >
              <Controller
                control={control}
                name="delivery_type"
                defaultValue={DeliveryType.SHIPPING}
                render={({ onChange, value, onBlur }) => (
                  <HTMLSelect
                    options={Object.values(DeliveryType).map((d) => ({
                      value: d,
                      label: formatDeliveryType(intl, d),
                    }))}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </FormField>
          )}

          {!lockOrder && (
            <FormField
              className="col-span-9"
              label={intl.formatMessage({
                defaultMessage: 'Invoice address',
              })}
              inline={true}
            >
              <Controller
                control={control}
                name="useDefaultAddress"
                defaultValue={true}
                render={({ onChange, value, onBlur }) => (
                  <Checkbox
                    label={intl.formatMessage({
                      defaultMessage: 'Use default on customer',
                    })}
                    marginBottom={majorScale(2)}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              {watch('useDefaultAddress') === false && (
                <Box>
                  <AddressFormControls
                    name="invoice_address"
                    showCompanySelect
                  />
                </Box>
              )}
            </FormField>
          )}

          {!lockOrder && (
            <FormField
              className="col-span-9"
              label={intl.formatMessage({
                defaultMessage: 'Delivery address',
              })}
              inline={true}
            >
              <Controller
                control={control}
                name="sameAddressAsInvoice"
                defaultValue={true}
                render={({ onChange, value, onBlur }) => (
                  <Checkbox
                    label={intl.formatMessage({
                      defaultMessage: 'Same as invoice address',
                    })}
                    marginBottom={majorScale(2)}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />

              {watch('sameAddressAsInvoice') === false && (
                <Box>
                  <AddressFormControls
                    name="delivery_address"
                    showCompanySelect
                  />
                </Box>
              )}
            </FormField>
          )}
        </Grid>

        <Grid
          borderTop={`2px solid ${defaultColors.gray[100]}`}
          padding={majorScale(2)}
        >
          <Heading size={300} className="col-span-12">
            <FormattedMessage defaultMessage="Administration" />
          </Heading>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({
              defaultMessage: 'Reference',
            })}
            inline={true}
          >
            <InputGroup
              placeholder={intl.formatMessage({
                defaultMessage: 'A reference for the sale',
              })}
              ref={register}
              defaultValue=""
              name="reference"
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({
              defaultMessage: 'Description',
            })}
            inline={true}
          >
            <TextArea
              placeholder={intl.formatMessage({
                defaultMessage: 'A description of the sale',
              })}
              maxLength={500}
              ref={register}
              defaultValue=""
              name="description"
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({
              defaultMessage: 'Responsible',
            })}
            inline={true}
            hint={intl.formatMessage({
              defaultMessage:
                'Choose a person from your team to be responsible for the sale',
            })}
          >
            <Controller
              control={control}
              name="responsible"
              defaultValue={[]}
              render={({ onChange, value, onBlur }) => (
                <UsersInput
                  onBlur={onBlur}
                  value={value}
                  onChange={(users) => {
                    onChange(users.map((c) => c.id));
                  }}
                />
              )}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({
              defaultMessage: 'Tags',
            })}
            inline
          >
            <Controller
              control={control}
              name="tags"
              defaultValue={[]}
              render={({ onChange, value, onBlur }) => (
                <TagInput value={value} onChange={onChange} onBlur={onBlur} />
              )}
            />
          </FormField>
        </Grid>
      </FormProvider>
    </Dialog>
  );
};
